import { useEffect, useState } from 'react';

/**
 * Countdown helpers for the HeroBannerV4 FOMO card («Beställ inom» — days :
 * hours : minutes : seconds to an order deadline).
 *
 * - `parseDeadline` — a date-only `YYYY-MM-DD` is the END of that local day
 *   (the DS FomoBlock convention, so a timer never flips a day early); any
 *   other string goes through `new Date`. Nothing → null.
 * - `countdownParts` — pure: the remaining time as zero-padded strings; a
 *   missing / invalid / passed deadline gives `00 00 00 00` (user decision
 *   2026-10-27: the card shows zeros rather than hiding the block).
 * - `useCountdown` — ticks once a second only while the deadline is ahead,
 *   stops itself at zero, cleared on unmount / deadline change.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const parseDeadline = (deadline) => {
  if (!deadline) return null;
  return DATE_ONLY.test(deadline) ? new Date(`${deadline}T23:59:59`) : new Date(deadline);
};

const pad = (n) => String(n).padStart(2, '0');
const ZEROS = { days: '00', hours: '00', minutes: '00', seconds: '00' };

export const countdownParts = (deadline, now = Date.now()) => {
  const target = parseDeadline(deadline);
  const ms = target ? target.getTime() - now : NaN;
  if (Number.isNaN(ms) || ms <= 0) return { ...ZEROS };
  const total = Math.floor(ms / 1000);
  return {
    days: pad(Math.floor(total / 86400)),
    hours: pad(Math.floor((total % 86400) / 3600)),
    minutes: pad(Math.floor((total % 3600) / 60)),
    seconds: pad(total % 60),
  };
};

export const useCountdown = (deadline) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const target = parseDeadline(deadline);
    const end = target ? target.getTime() : NaN;
    setNow(Date.now());
    if (Number.isNaN(end) || end <= Date.now()) return undefined;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= end) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  return countdownParts(deadline, now);
};
